import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RetailersService, FilterRequest, FilterRequestForCombo, MarketResponse, MarketsService, RetailerTypesService, RoleResponse, RolesService, SettingsService, SpecificationRequest, SpecificationResponsePaginatedList, SpecificationType } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-specifications-feature-settings',
    templateUrl: './specifications-feature-settings.component.html',
    styleUrls: ['./specifications-feature-settings.component.scss'],
    host: {
        class: 'app-specifications-feature-settings'
    },
    standalone: false
})
export class SpecificationsSettingsComponent implements OnInit {

  // local pagination
  paginatorFirst: number = 0;
  paginatorRows: number = 10;

  private readonly fetchPageSize: number = 200;

  private allSpecifications: any[] = [];

  get totalRecords(): number {
    return this.allSpecifications.length;
  }

  get paginatedSpecifications(): any[] {
    const startIndex = this.paginatorFirst;
    const endIndex = startIndex + this.paginatorRows;

    return this.allSpecifications.slice(startIndex, endIndex);
  }

  protected filter: FilterRequest = {
    pageSize: this.fetchPageSize,
    pageNumber: 1
  };

  page: SpecificationResponsePaginatedList = {};
  showCreateSpecModal = false;
  showViewSpecModal = false;
  selectedSpecId: number = 0;
  activeTab: number[] = [0]; // accordion first tab open (supports multiple)

  specificationTypes = [
    { value: SpecificationType.WarrantyDurationMonths },
    { value: SpecificationType.CanModifyPrice },
    { value: SpecificationType.ReturnSaleMonths },
    { value: SpecificationType.CreateSaleMonths },
    { value: SpecificationType.CanAssignCustomers },
    { value: SpecificationType.CanExtendExpiredWarranty },
    { value: SpecificationType.D2CWarrantyDurationMonths },
    { value: SpecificationType.GraceIntegration },
    { value: SpecificationType.GraceIntegrationCta }
  ];

  specificationTypeOptions = this.specificationTypes;
  specificationTypeTotalRecords = this.specificationTypeOptions.length;
  specificationTypeRowsPerPageOption = 10;

  filterOperator = FilterOperator;

  rolesList: any[] = [];
  rolesTotalRecords: number = 0;
  rolesRowsPerPageOption: number = 10;

  marketsList: any[] = [];
  marketsTotalRecords: number = 0;
  marketsRowsPerPageOption: number = 10;

  retailersList: any[] = [];
  retailersTotalRecords: number = 0;
  retailersRowsPerPageOption: number = 10;

  retailerTypesList: any[] = [];
  retailerTypesTotalRecords: number = 0;
  retailerTypesRowsPerPageOption: number = 10;

  stateOptions = [
    { label: "On", value: true },
    { label: "Off", value: false }
  ];

  public specificationForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    specificationType: [[], [Validators.required]],
    restriction: [null, [Validators.required]],
    role: [[]],
    market: [[]],
    retailer: [[]],
    retailerType: [[]]
  });

  public updateSpecificationForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    specificationType: [[], [Validators.required]],
    restriction: [null, [Validators.required]],
    role: [[]],
    market: [[]],
    retailer: [[]],
    retailerType: [[]]
  });

  constructor(
    private fb: FormBuilder,
    public settingsService: SettingsService,
    public rolesService: RolesService,
    public marketsService: MarketsService,
    public retailersService: RetailersService,
    public retailerTypeService: RetailerTypesService
  ) { }


  ngOnInit() {
    this.init();
  }

  async init(): Promise<void> {
    this.reset();
    await this.get();
    await this.getRoles({ pageIndex: 1, pageSize: this.rolesRowsPerPageOption, search: '' });
    await this.getMarkets({ pageIndex: 1, pageSize: this.marketsRowsPerPageOption, search: '' });
    await this.getRetailerTypes({ pageIndex: 1, pageSize: this.retailerTypesRowsPerPageOption, search: '' });
    await this.getRetailers({ pageIndex: 1, pageSize: this.retailersRowsPerPageOption, search: '' });
  }

  async get() {
    this.allSpecifications = await this.fetchAllSpecifications();
    this.normalizePaginatorFirst();
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  private async fetchAllSpecifications(): Promise<any[]> {
    const results: any[] = [];

    let pageNumber = 1;

    while (true) {
      const request: FilterRequest = {
        ...this.filter,
        pageNumber: pageNumber,
        pageSize: this.fetchPageSize
      };

      const page = await firstValueFrom(this.settingsService.apiSettingsSpecificationFilterPost(request));

      const pageItems = page.results ?? [];

      const totalCount = page.totalCount ?? results.length;
      results.push(...pageItems);

      const receivedNoItems = pageItems.length === 0;
      const receivedAllItems = results.length >= totalCount;

      if (receivedNoItems || receivedAllItems) {
        this.page = page;
        break;
      }

      pageNumber += 1;
    }

    return results;
  }

  private normalizePaginatorFirst(): void {
    if (this.paginatorRows <= 0) {
      this.paginatorRows = 10;
    }

    if (this.totalRecords <= 0) {
      this.paginatorFirst = 0;
      return;
    }

    const lastPageIndex = Math.max(0, Math.ceil(this.totalRecords / this.paginatorRows) - 1);
    const maxFirstIndex = lastPageIndex * this.paginatorRows;

    if (this.paginatorFirst < 0) {
      this.paginatorFirst = 0;
      return;
    }

    if (this.paginatorFirst > maxFirstIndex) {
      this.paginatorFirst = maxFirstIndex;
    }
  }

  onPageChange(event: any) {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;
  }

  reset() {
    this.specificationForm.reset({
      name: '',
      specificationType: [],
      restriction: null,
      role: [],
      market: [],
      retailer: [],
      retailerType: []
    });

    this.updateSpecificationForm.reset({
      name: '',
      specificationType: [],
      restriction: null,
      role: [],
      retailer: [],
      retailerType: []
    });
  }

  cancelCreateSpec() {
    this.showCreateSpecModal = false;
    this.reset();
  }

  displayCreateSpecModalbox() {
    this.showCreateSpecModal = true;
  }

  async onRemoveSpecification(event: MouseEvent, specificationId: number) {
    event.preventDefault();
    await firstValueFrom(this.settingsService.apiSettingsSpecificationDeleteIdDelete(specificationId));
    this.reset();
    await this.get();
  }

  async createSpecification() {
    const data: SpecificationRequest = {
      name: this.specificationForm.controls['name'].value,
      restriction: this.specificationForm.controls['restriction'].value,
      specificationType: this.specificationForm.controls['specificationType'].value?.[0],
      roleId: this.specificationForm.controls['role'].value?.[0],
      marketId: this.specificationForm.controls['market'].value?.[0],
      retailerId: this.specificationForm.controls['retailer'].value?.[0],
      retailerTypeId: this.specificationForm.controls['retailerType'].value?.[0]
    };

    await firstValueFrom(this.settingsService.apiSettingsSpecificationCreatePost(data));
    this.showCreateSpecModal = false;
    this.reset();
    await this.get();
  }

  onViewSpecification(event: MouseEvent, data: any) {
    event.preventDefault();
    this.showViewSpecModal = true;
    this.selectedSpecId = data.id;
    if (data.role?.id) {
      const exists = this.rolesList.some((r: any) => r.id === data.role.id);
      if (!exists) {
        this.rolesList = [{ id: data.role.id, text: data.role.name}, ...this.rolesList];
      }
    }

    if (data.market?.id) {
      const exists = this.marketsList.some((m: any) => m.id === data.market.id);
      if (!exists) {
        this.marketsList = [{ id: data.market.id, text: `${data.market.name} (${data.market.code})`}, ...this.marketsList];
      }
    }

    if (data.retailerType?.id) {
      const exists = this.retailerTypesList.some((retailerType: any) => {
        return retailerType.id === data.retailerType.id;
      });

      if (!exists) {
        this.retailerTypesList = [
          {
            id: data.retailerType.id,
            text: `${data.retailerType.name} (${data.retailerType.code})`
          },
          ...this.retailerTypesList
        ];
      }
    }

    if (data.retailer?.id) {
      const exists = this.retailersList.some((m: any) => m.id === data.retailer.id);
      if (!exists) {
        this.retailersList = [{ id: data.retailer.id, text: `${data.retailer.name} (${data.retailer.code})`}, ...this.retailersList];
      }
    }

    this.updateSpecificationForm.patchValue({
      name: data.name,
      restriction: data.restriction,
      specificationType: data.specificationType ? [data.specificationType] : [],
      role: data.role?.id ? [data.role.id] : [],
      market: data.market?.id ? [data.market.id] : [],
      retailer: data.retailer?.id ? [data.retailer.id] : [],
      retailerType: data.retailerType?.id ? [data.retailerType.id] : []
    });
  }

  async saveSpecification() {
    const data: SpecificationRequest = {
      name: this.updateSpecificationForm.controls['name'].value,
      restriction: this.updateSpecificationForm.controls['restriction'].value,
      specificationType: this.updateSpecificationForm.controls['specificationType'].value?.[0],
      roleId: this.updateSpecificationForm.controls['role'].value?.[0],
      marketId: this.updateSpecificationForm.controls['market'].value?.[0],
      retailerId: this.updateSpecificationForm.controls['retailer'].value?.[0],
      retailerTypeId: this.updateSpecificationForm.controls['retailerType'].value?.[0]
    };

    await firstValueFrom(this.settingsService.apiSettingsSpecificationUpdateIdPut(this.selectedSpecId, data));
    this.showViewSpecModal = false;
    this.reset();
    this.selectedSpecId = 0;
    await this.get();
  }

  cancelViewSpecification() {
    this.showViewSpecModal = false;
    this.reset();
    this.selectedSpecId = 0;
  }

  async onStateChange(event: any, specification: any) {
    const stateNewValue = event.checked;

    if (!stateNewValue) {
      await firstValueFrom(this.settingsService.apiSettingsSpecificationIdDeactivatePut(specification.id));
    } else if (stateNewValue) {
      await firstValueFrom(this.settingsService.apiSettingsSpecificationIdActivatePut(specification.id));
    }

    await this.get();
  }

  onLazyLoadRoles(event: any) {
    this.getRoles(event);
  }

  async getRoles(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.rolesRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    filters.push({
      propertyName: 'isActive',
      operator: this.filterOperator.EQUALS,
      values: ['true']
    });

    payload.additionalFilters = filters;

    const rolesPage = await firstValueFrom(this.rolesService.apiRolesFilterComboPost(payload));
    this.rolesList = rolesPage.results ?? [];
    this.rolesTotalRecords = rolesPage.totalCount ?? 0;
  }

  onLazyLoadMarkets(event: any) {
    this.getMarkets(event);
  }

  async getMarkets(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.marketsRowsPerPageOption
    }
    if (search) {
      payload.textFilter = search;
    }

    const marketsPage = await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(payload));
    this.marketsList = marketsPage.results ?? [];
    this.marketsTotalRecords = marketsPage.totalCount ?? 0;
  }

  onLazyLoadRetailerTypes(event: any) {
    this.getRetailerTypes(event);
  }

  onLazyLoadRetailers(event: any) {
    this.getRetailers(event);
  }

  async getRetailerTypes(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.retailerTypesRowsPerPageOption
    };

    if (search) {
      payload.textFilter = search;
    }

    const retailerTypesPage = await firstValueFrom(
      this.retailerTypeService.apiRetailerTypesFilterComboPost(payload)
    );

    this.retailerTypesList = retailerTypesPage.results ?? [];
    this.retailerTypesTotalRecords = retailerTypesPage.totalCount ?? 0;
  }

    async getRetailers(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.retailersRowsPerPageOption
    };

    if (search) {
      payload.textFilter = search;
    }

    const retailersPage = await firstValueFrom(
      this.retailersService.apiRetailersFilterComboPost(payload)
    );

    this.retailersList = retailersPage.results ?? [];
    this.retailersTotalRecords = retailersPage.totalCount ?? 0;
  }
}
